import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";

import type { CurrentSettingsResourceIdentity } from "@/config/sitemap";

import type { AskDecodaDecision, ChatHistoryEntry } from "@/types/chat-thread";

export interface AskDecodaResumeCommand {
  interruptId: string;
  checkpointId: string;
  decisions: AskDecodaDecision[];
}

export enum AskDecodaRunKind {
  SEND = "SEND",
  REATTACH = "REATTACH",
  RESUME = "RESUME",
}

export interface AskDecodaPinIntent {
  messageId: string;
  visualizationId: string;
  dashboardId?: string;
}

export interface AskDecodaSendPayload {
  pinIntent?: AskDecodaPinIntent;
  message: string;
  currentPage?: string;
  setupItemId?: string;
  currentSettingsResource: CurrentSettingsResourceIdentity | null;
  timezone: string;
  files?: ChatHistoryEntry["files"];
  createdDate: string;
}

export interface AskDecodaLiveRun {
  tenantSubDomain: string;
  runId: string;
  kind: AskDecodaRunKind;
  threadId: string;
  chatId: string;
  requestId: string;
  runtimeEpoch: number;
  createdDate: string;
  payload?: AskDecodaSendPayload;
  responseChatId?: string;
  resume?: AskDecodaResumeCommand;
  accepted?: Promise<void>;
  finished?: Promise<void>;
  resolveAccepted?: () => void;
  resolveFinished?: () => void;
  rejectFinished?: (error: unknown) => void;
  onAccepted?: () => void;
}

interface StartLiveRunInput {
  tenantSubDomain: string;
  threadId: string;
  chatId: string;
  requestId: string;
  runtimeEpoch: number;
  payload: AskDecodaSendPayload;
  onAccepted?: () => void;
}

interface StartReattachRunInput {
  tenantSubDomain: string;
  threadId: string;
  chatId: string;
  responseChatId?: string;
}

interface StartResumeRunInput {
  tenantSubDomain: string;
  threadId: string;
  chatId: string;
  responseChatId: string;
  runtimeEpoch: number;
  resume: AskDecodaResumeCommand;
}

interface AskDecodaStore {
  tenantScope: string | undefined | null;
  ensureTenantScope: (tenantSubDomain: string | undefined) => void;
  initialQuestion: string;
  initialFiles: File[];
  preserveInitialQuestionOnSidebarClose: boolean;
  activeThreadId: string | null;
  suppressResumeForThreadId: string | null;
  pendingSetupItemId: string | null;
  setupItemIdByThread: Record<string, string>;
  runtimeEpoch: number;
  settingsHandledCompletions: Record<string, true>;
  claimSettingsCompletion: (key: string) => boolean;
  settingsRefreshing: Record<string, boolean>;
  settingsRefreshWarnings: Record<string, string>;
  setSettingsRefreshing: (patchId: string, busy: boolean) => void;
  setSettingsRefreshWarning: (patchId: string, warning: string) => void;
  approvalDrafts: Record<string, Record<number, AskDecodaDecision>>;
  stageApprovalDecision: (
    key: string,
    index: number,
    decision: AskDecodaDecision,
  ) => void;
  liveRunsByThreadId: Record<string, AskDecodaLiveRun>;
  stoppingOperationsByThreadId: Record<string, string>;
  cancelledResponses: Record<string, true>;
  markResponseCancelled: (key: string) => void;
  beginStopping: (threadId: string, operationId: string) => boolean;
  finishStopping: (threadId: string, operationId: string) => void;

  setInitialQuestion: (
    question: string,
    files?: File[],
    options?: { preserveOnSidebarClose?: boolean },
  ) => void;
  handleSidebarClosed: () => void;
  consumeInitialQuestion: (question: string) => File[] | null;
  setActiveThreadId: (threadId: string | null) => void;
  setSetupItem: (setupItemId: string) => void;
  setupItemIdForThread: (threadId: string) => string | null;
  setSuppressResumeForThreadId: (threadId: string | null) => void;
  startLiveRun: (input: StartLiveRunInput) => {
    accepted: Promise<void>;
    finished: Promise<void>;
  };
  startReattachRun: (input: StartReattachRunInput) => void;
  startResumeRun: (input: StartResumeRunInput) => { finished: Promise<void> };
  finishLiveRun: (threadId: string, runId: string) => void;
  cancelThreadRun: (threadId: string) => void;
  clearConversation: (options?: { abortCurrentStream?: boolean }) => void;
  startNewConversation: (options?: { abortCurrentStream?: boolean }) => void;
  clearAllConversations: () => void;
}

export function hasActiveAskDecodaConversation(
  state: Pick<AskDecodaStore, "activeThreadId">,
): boolean {
  return state.activeThreadId !== null;
}

const streamAbortsByThreadId = new Map<string, Set<() => void>>();

export function registerAskDecodaStreamAbort(
  threadId: string,
  abort: () => void,
): () => void {
  const aborts = streamAbortsByThreadId.get(threadId) ?? new Set();
  aborts.add(abort);
  streamAbortsByThreadId.set(threadId, aborts);
  return () => {
    aborts.delete(abort);
    if (aborts.size === 0) streamAbortsByThreadId.delete(threadId);
  };
}

function abortAskDecodaThreadStreams(threadId: string): void {
  streamAbortsByThreadId.get(threadId)?.forEach((abort) => abort());
}

function abortAllAskDecodaStreams(): void {
  streamAbortsByThreadId.forEach((aborts) =>
    aborts.forEach((abort) => abort()),
  );
}

function omitRun(
  runs: Record<string, AskDecodaLiveRun>,
  threadId: string,
): Record<string, AskDecodaLiveRun> {
  const { [threadId]: removed, ...rest } = runs;
  void removed;
  return rest;
}

export const useAskDecodaStore = create<AskDecodaStore>((set, get) => ({
  tenantScope: null,
  ensureTenantScope: (tenantSubDomain) => {
    const previous = get().tenantScope;
    if (previous === tenantSubDomain) return;
    // Claim ownership before abort callbacks run. A later host effect for this
    // same tenant must not clear work already admitted by a mounted consumer.
    set({ tenantScope: tenantSubDomain });
    if (previous !== null) get().clearAllConversations();
  },
  initialQuestion: "",
  initialFiles: [],
  preserveInitialQuestionOnSidebarClose: false,
  activeThreadId: null,
  suppressResumeForThreadId: null,
  pendingSetupItemId: null,
  setupItemIdByThread: {},
  runtimeEpoch: 0,
  settingsHandledCompletions: {},
  claimSettingsCompletion: (key) => {
    if (get().settingsHandledCompletions[key]) return false;
    set((state) => ({
      settingsHandledCompletions: {
        ...state.settingsHandledCompletions,
        [key]: true,
      },
    }));
    return true;
  },
  settingsRefreshing: {},
  settingsRefreshWarnings: {},
  setSettingsRefreshing: (patchId, busy) =>
    set((state) => ({
      settingsRefreshing: { ...state.settingsRefreshing, [patchId]: busy },
    })),
  setSettingsRefreshWarning: (patchId, warning) =>
    set((state) => ({
      settingsRefreshWarnings: {
        ...state.settingsRefreshWarnings,
        [patchId]: warning,
      },
    })),
  approvalDrafts: {},
  stageApprovalDecision: (key, index, decision) =>
    set((state) => ({
      approvalDrafts: {
        ...state.approvalDrafts,
        [key]: { ...state.approvalDrafts[key], [index]: decision },
      },
    })),
  liveRunsByThreadId: {},
  stoppingOperationsByThreadId: {},
  cancelledResponses: {},
  markResponseCancelled: (key) =>
    set((state) => ({
      cancelledResponses: { ...state.cancelledResponses, [key]: true },
    })),
  beginStopping: (threadId, operationId) => {
    if (get().stoppingOperationsByThreadId[threadId] === operationId)
      return false;
    set((state) => ({
      stoppingOperationsByThreadId: {
        ...state.stoppingOperationsByThreadId,
        [threadId]: operationId,
      },
    }));
    return true;
  },
  finishStopping: (threadId, operationId) => {
    if (get().stoppingOperationsByThreadId[threadId] !== operationId) return;
    set((state) => {
      const remaining = { ...state.stoppingOperationsByThreadId };
      delete remaining[threadId];
      return { stoppingOperationsByThreadId: remaining };
    });
  },

  setInitialQuestion: (question, files, options) => {
    set({
      initialQuestion: question,
      initialFiles: files?.length ? files : [],
      preserveInitialQuestionOnSidebarClose:
        options?.preserveOnSidebarClose === true,
    });
  },

  handleSidebarClosed: () => {
    if (get().preserveInitialQuestionOnSidebarClose) {
      set({ preserveInitialQuestionOnSidebarClose: false });
      return;
    }
    set({ initialQuestion: "", initialFiles: [] });
  },

  consumeInitialQuestion: (question) => {
    const state = get();
    if (state.initialQuestion !== question) return null;
    if (!state.initialQuestion && state.initialFiles.length === 0) return null;
    const files = state.initialFiles;
    set({
      initialQuestion: "",
      initialFiles: [],
      preserveInitialQuestionOnSidebarClose: false,
    });
    return files;
  },

  setActiveThreadId: (threadId) => set({ activeThreadId: threadId }),
  setSetupItem: (setupItemId) => set({ pendingSetupItemId: setupItemId }),

  setupItemIdForThread: (threadId) => {
    const { pendingSetupItemId, setupItemIdByThread } = get();
    if (setupItemIdByThread[threadId]) return setupItemIdByThread[threadId];
    if (!pendingSetupItemId) return null;
    set({
      pendingSetupItemId: null,
      setupItemIdByThread: {
        ...setupItemIdByThread,
        [threadId]: pendingSetupItemId,
      },
    });
    return pendingSetupItemId;
  },

  setSuppressResumeForThreadId: (threadId) =>
    set({ suppressResumeForThreadId: threadId }),

  startLiveRun: (input) => {
    const existing = get().liveRunsByThreadId[input.threadId];
    if (existing?.accepted && existing.finished) {
      return { accepted: existing.accepted, finished: existing.finished };
    }
    let resolveAccepted!: () => void;
    let resolveFinished!: () => void;
    const accepted = new Promise<void>((resolve) => {
      resolveAccepted = resolve;
    });
    const finished = new Promise<void>((resolve) => {
      resolveFinished = resolve;
    });
    const run: AskDecodaLiveRun = {
      runId: uuidv4(),
      kind: AskDecodaRunKind.SEND,
      tenantSubDomain: input.tenantSubDomain,
      threadId: input.threadId,
      chatId: input.chatId,
      requestId: input.requestId,
      runtimeEpoch: input.runtimeEpoch,
      createdDate: input.payload.createdDate,
      payload: input.payload,
      accepted,
      finished,
      resolveAccepted,
      resolveFinished,
      onAccepted: input.onAccepted,
    };
    set((state) => ({
      liveRunsByThreadId: { ...state.liveRunsByThreadId, [run.threadId]: run },
    }));
    return { accepted, finished };
  },

  startResumeRun: (input) => {
    const existing = get().liveRunsByThreadId[input.threadId];
    if (existing)
      return {
        finished: Promise.reject(new Error("An approval is already running.")),
      };
    if (get().runtimeEpoch !== input.runtimeEpoch)
      return { finished: Promise.reject(new Error("This approval is stale.")) };
    let resolveFinished!: () => void;
    let rejectFinished!: (error: unknown) => void;
    const finished = new Promise<void>((resolve, reject) => {
      resolveFinished = resolve;
      rejectFinished = reject;
    });
    const run: AskDecodaLiveRun = {
      ...input,
      runId: uuidv4(),
      kind: AskDecodaRunKind.RESUME,
      requestId: `local:${input.chatId}`,
      createdDate: new Date().toISOString(),
      finished,
      resolveFinished,
      rejectFinished,
    };
    set((state) => ({
      liveRunsByThreadId: { ...state.liveRunsByThreadId, [run.threadId]: run },
    }));
    return { finished };
  },

  startReattachRun: (input) => {
    if (get().liveRunsByThreadId[input.threadId]) return;
    const run: AskDecodaLiveRun = {
      runId: uuidv4(),
      kind: AskDecodaRunKind.REATTACH,
      tenantSubDomain: input.tenantSubDomain,
      threadId: input.threadId,
      chatId: input.chatId,
      responseChatId: input.responseChatId,
      requestId: `reattach:${input.chatId}`,
      runtimeEpoch: get().runtimeEpoch,
      createdDate: new Date().toISOString(),
    };
    set((state) => ({
      liveRunsByThreadId: { ...state.liveRunsByThreadId, [run.threadId]: run },
    }));
  },

  finishLiveRun: (threadId, runId) => {
    const run = get().liveRunsByThreadId[threadId];
    if (run?.runId !== runId) return;
    run?.resolveAccepted?.();
    run?.resolveFinished?.();
    set((state) => ({
      liveRunsByThreadId: omitRun(state.liveRunsByThreadId, threadId),
    }));
  },

  cancelThreadRun: (threadId) => abortAskDecodaThreadStreams(threadId),

  clearConversation: (options) => {
    const threadId = get().activeThreadId;
    if (options?.abortCurrentStream === true && threadId) {
      abortAskDecodaThreadStreams(threadId);
    }
    set({
      initialQuestion: "",
      initialFiles: [],
      preserveInitialQuestionOnSidebarClose: false,
      activeThreadId: null,
      pendingSetupItemId: null,
      suppressResumeForThreadId: null,
    });
  },

  startNewConversation: (options) => get().clearConversation(options),

  clearAllConversations: () => {
    abortAllAskDecodaStreams();
    Object.values(get().liveRunsByThreadId).forEach((run) => {
      run.resolveAccepted?.();
      run.resolveFinished?.();
    });
    set({
      activeThreadId: null,
      pendingSetupItemId: null,
      runtimeEpoch: get().runtimeEpoch + 1,
      approvalDrafts: {},
      settingsHandledCompletions: {},
      settingsRefreshing: {},
      settingsRefreshWarnings: {},
      setupItemIdByThread: {},
      suppressResumeForThreadId: null,
      initialQuestion: "",
      initialFiles: [],
      liveRunsByThreadId: {},
      stoppingOperationsByThreadId: {},
      cancelledResponses: {},
    });
  },
}));
